import { useState } from 'react'
import { Box } from '@mui/material'
import toast from 'react-hot-toast'
import {
  useDeleteSchedulerSOR,
  useScheduleSOR,
  useUpdateSchedulerSOR,
  useUploadScheduleDataFile,
} from './SOR.data'
import { KitchenCycleEditor } from '../KitchenCycleEditor'
import { SchedulerKitchenCyclesTable } from '../SchedulerKitchenCycles.muiTable'
import { SchedulerKitchenCycleRowType } from '../Scheduler.types'

const API = 'kitchen-cycles'

// Master (tenant-wide) kitchen cycles: SchedulerApp/Inputs/KitchenCyclesDelta/.
// Same editor as the per-schedule Raw Data view, fed from the master packing
// routes and tanks. New schedules are seeded from it at creation time
// (`folders` in SchedulerService/models/scheduler_model.py), so it reaches
// schedules created after an edit, not existing ones.
export const SORKitchenCycles = () => {
  const {
    data: cycleResponse = { data: [], predicate: undefined },
    isLoading,
  } = useScheduleSOR(API)
  const cycleRows = (cycleResponse.data ?? []) as SchedulerKitchenCycleRowType[]
  const { data: routesResponse = { data: [] } } =
    useScheduleSOR('packing-routes')
  const { data: tanksResponse = { data: [] } } = useScheduleSOR('tanks')

  const { mutate: saveData, isLoading: isSaving } = useUpdateSchedulerSOR(
    () => toast.success('Kitchen cycle saved'),
    () => toast.error('Failed to save the kitchen cycle')
  )
  const { mutateAsync: deleteData, isLoading: isDeleting } =
    useDeleteSchedulerSOR(
      () => toast.success('Rows deleted'),
      () => toast.error('Failed to delete rows')
    )
  const [updateRowsStatus, setUpdateRowsStatus] = useState({})
  const { mutate: uploadData, isLoading: isUploading } =
    useUploadScheduleDataFile(
      (mergeInfo) => {
        setUpdateRowsStatus(mergeInfo)
        toast.success('Data uploaded successfully')
      },
      () => toast.error('Failed to upload data')
    )

  const onSave = (rows: Record<number, SchedulerKitchenCycleRowType>) =>
    saveData({ data: rows, API })
  const onDelete = async (rows: SchedulerKitchenCycleRowType[]) => {
    await deleteData({ rows, API })
  }

  return (
    <Box>
      <KitchenCycleEditor
        cycleRows={cycleRows}
        routes={routesResponse.data ?? []}
        tanks={tanksResponse.data ?? []}
        onSave={onSave}
        onDelete={onDelete}
        isBusy={isSaving || isDeleting}
      />
      <SchedulerKitchenCyclesTable
        data={cycleRows}
        isLoading={isLoading}
        updateTableData={onSave}
        uploadScheduleData={(file: File) => uploadData({ file, API })}
        isUploading={isUploading}
        isSaving={isSaving}
        updateRowsStatus={updateRowsStatus}
        predicate={cycleResponse.predicate}
        deleteTableData={onDelete}
        isDeleting={isDeleting}
      />
    </Box>
  )
}
